import { usePostHog } from 'posthog-js/react'
import { useCallback, useRef } from 'react'

type AssertSnakeCaseKeys<T> = {
  [K in keyof T]: K extends string
    ? Lowercase<K> extends K
      ? K extends `${string}-${string}`
        ? ['Error: property key must be snake_case (no hyphens), got:', K]
        : T[K]
      : ['Error: property key must be snake_case, got:', K]
    : T[K]
}

type EventMap = Record<string, undefined | Record<string, unknown>>

type TrackOptions = {
  lowercase?: boolean
}

const defaultTrackOptions: Required<TrackOptions> = {
  lowercase: true,
}

const normalizeProperties = (
  properties: Record<string, unknown> | undefined,
  options: Required<TrackOptions>
): Record<string, unknown> | undefined => {
  if (!properties) {
    return undefined
  }
  if (!options.lowercase) {
    return properties
  }

  const normalized: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(properties)) {
    normalized[key] = typeof value === 'string' ? value.toLowerCase() : value
  }
  return normalized
}

type ValidateProps<T> = T extends Record<string, unknown> ? AssertSnakeCaseKeys<T> : T

type TrackArgs<T extends EventMap, E extends keyof T & string> = T[E] extends undefined
  ? [event: E] | [event: E, properties: null, options: TrackOptions]
  :
      | [event: E, properties: ValidateProps<T[E]>]
      | [event: E, properties: ValidateProps<T[E]>, options: TrackOptions]

type TrackFn<T extends EventMap> = <E extends keyof T & string>(...args: TrackArgs<T, E>) => void

/**
 * Generic, type-safe PostHog event tracking hook.
 *
 * Parameterize with an app-specific event map to get autocomplete on event
 * names and compile-time enforcement of required properties. Property keys must
 * be snake_case (per the PostHog naming convention); non-snake_case keys fail to
 * compile via {@link AssertSnakeCaseKeys}.
 *
 * - Void events (`undefined` in the map): `track('event_name')`
 * - Property events: `track('event_name', { ...props })`
 *
 * String property values are lowercased by default.
 * Pass `null` as properties and `{ lowercase: false }` as the third argument
 * to preserve original casing for void events, or pass options as the third
 * argument for property events.
 *
 * The returned function is **referentially stable** across renders.
 *
 * @example
 * ```ts
 * type MyEvents = {
 *   button_clicked: undefined
 *   item_created: { item_type: string }
 * }
 *
 * const track = useTrack<MyEvents>()
 *
 * track('button_clicked')
 * track('button_clicked', null, { lowercase: false })
 * track('item_created', { item_type: 'widget' })
 * track('item_created', { item_type: 'Widget' }, { lowercase: false })
 * ```
 */
export const useTrack = <T extends EventMap>(): TrackFn<T> => {
  const posthog = usePostHog()

  const trackRef = useRef<TrackFn<T>>(null as unknown as TrackFn<T>)
  trackRef.current = ((...args: unknown[]) => {
    const [event, properties, options] = args as [
      string,
      Record<string, unknown> | null | undefined,
      TrackOptions | undefined,
    ]
    const opts = { ...defaultTrackOptions, ...options }
    posthog.capture(event, normalizeProperties(properties ?? undefined, opts))
  }) as TrackFn<T>

  return useCallback(
    ((...args: unknown[]) => {
      ;(trackRef.current as (...a: unknown[]) => void)(...args)
    }) as TrackFn<T>,
    []
  )
}
